import {
    config,
    decryptAutoDetect,
    encryptAndDecryptXorString,
    encryptAesGcm,
    isAesGcmEncrypted,
    isValidDecryptedToken,
} from "JS/Config";
import { appConstants } from "./Constants";
import moment from "moment";

export const getPlaylistMigrated = () =>
    localStorage.getItem(appConstants.localStorage.playlistMigrated);

export const setPlaylistMigrated = () =>
    localStorage.setItem(appConstants.localStorage.playlistMigrated, "true");

export const getLoggedInUserMigrated = () =>
    localStorage.getItem(appConstants.localStorage.loggedInUserMigrated);

export const getIsRedirectedToAmmwayConsentForm = () =>
    localStorage.getItem(
        appConstants.localStorage.isRedirectedToAmmwayConsentForm,
    );

export const getIsLtdConsentFormOpened = () =>
    localStorage.getItem(appConstants.localStorage.isLtdConsentFormOpened);

export const getIsRedirectedToPurchaseBundlePage = () =>
    localStorage.getItem(
        appConstants.localStorage.isRedirectedToPurchaseBundlePage,
    );

export const getIsRedirectedToBundleOptInPage = () =>
    localStorage.getItem(
        appConstants.localStorage.isRedirectedToBundleOptInPage,
    );

export const getIsRedirectedToBundleSubsPage = () =>
    localStorage.getItem(
        appConstants.localStorage.isRedirectedToBundleSubsPage,
    );

export const getIsRedirectingToSso = () =>
    localStorage.getItem(appConstants.localStorage.isRedirectingToSso);

export const getResetConsentIndexDbStatus = () =>
    localStorage.getItem(appConstants.localStorage.resetConsentIndexDbStatus);

export const getUserFirstName = () =>
    localStorage.getItem(appConstants.localStorage.firstName);

export const getUserLastName = () =>
    localStorage.getItem(appConstants.localStorage.lastName);

export const setLoggedInUserMigrated = (value: string) =>
    localStorage.setItem(appConstants.localStorage.loggedInUserMigrated, value);

const setTokenEncyptedMigrated = (value: string) =>
    localStorage.setItem(
        appConstants.localStorage.encryptedTokensMigrated,
        value,
    );

const getAesGcmMigrated = () =>
    localStorage.getItem(appConstants.localStorage.aesGcmMigrated);

const setAesGcmMigrated = (value: string) =>
    localStorage.setItem(appConstants.localStorage.aesGcmMigrated, value);

const getNgTokensAesGcmMigrated = () =>
    localStorage.getItem(appConstants.localStorage.ngTokensAesGcmMigrated);

const setNgTokensAesGcmMigrated = (value: string) =>
    localStorage.setItem(
        appConstants.localStorage.ngTokensAesGcmMigrated,
        value,
    );

export const setIsRedirectedToAmmwayConsentForm = (value: string) =>
    localStorage.setItem(
        appConstants.localStorage.isRedirectedToAmmwayConsentForm,
        value,
    );

export const setIsRedirectedToPurchaseBundlePage = (value: string) =>
    localStorage.setItem(
        appConstants.localStorage.isRedirectedToPurchaseBundlePage,
        value,
    );

export const setIsRedirectedToBundleOptInPage = (value: string) =>
    localStorage.setItem(
        appConstants.localStorage.isRedirectedToBundleOptInPage,
        value,
    );

export const setIsRedirectedToBundleSubsPage = (value: string) =>
    localStorage.setItem(
        appConstants.localStorage.isRedirectedToBundleSubsPage,
        value,
    );

export const setIsRedirectingToSso = (value: string) =>
    localStorage.setItem(appConstants.localStorage.isRedirectingToSso, value);

export const setIsLtdConsentFormOpened = (value: string) =>
    localStorage.setItem(
        appConstants.localStorage.isLtdConsentFormOpened,
        value,
    );

export const setResetConsentIndexDbStatus = (value: string) =>
    localStorage.setItem(
        appConstants.localStorage.resetConsentIndexDbStatus,
        value,
    );

const getTokenEncyptedMigrated = () =>
    localStorage.getItem(appConstants.localStorage.encryptedTokensMigrated);

export const migrateExistingValues = async (): Promise<void> => {
    if (getTokenEncyptedMigrated() !== "true") {
        await migrateLocalStorageValues();
        window.location.reload();
        return;
    }

    if (getAesGcmMigrated() !== "true") {
        await migrateXorToAesGcm();
        window.location.reload();
        return;
    }

    if (getNgTokensAesGcmMigrated() !== "true") {
        await migrateNgTokensXorToAesGcm();
        window.location.reload();
    }
};

const migrateLocalStorageValues = async (): Promise<void> => {
    const accessToken = localStorage.getItem("accessToken") || "";
    const refreshToken = localStorage.getItem("refreshToken") || "";
    const courseFirebaseToken =
        localStorage.getItem("courseFirebaseToken") || "";
    const webcastsFirebaseToken =
        localStorage.getItem("webcastsFirebaseToken") || "";

    // Only reroute legacy tokens that look like real plaintext; XOR-ciphertext/corrupt values are dropped so a bad value forces clean re-login instead of a silent invalid session.
    const validAccessToken = isValidDecryptedToken(accessToken)
        ? accessToken
        : "";
    const validRefreshToken = isValidDecryptedToken(refreshToken)
        ? refreshToken
        : "";

    await Promise.all([
        setAesEncryptedLocalStorageItem(
            validAccessToken,
            appConstants.localStorage.ngAccessToken,
        ),
        setAesEncryptedLocalStorageItem(
            validRefreshToken,
            appConstants.localStorage.ngRefreshToken,
        ),
        setAesEncryptedLocalStorageItem(
            webcastsFirebaseToken,
            appConstants.localStorage.webcastsFirebaseToken,
        ),
        setAesEncryptedLocalStorageItem(
            courseFirebaseToken,
            appConstants.localStorage.courseFirebaseToken,
        ),
    ]);

    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("courseFirebaseToken");
    localStorage.removeItem("webcastsFirebaseToken");
    setTokenEncyptedMigrated("true");
    setAesGcmMigrated("true");
    setNgTokensAesGcmMigrated("true");
};

const migrateXorToAesGcm = async (): Promise<void> => {
    const s3Key = config.s3.xor;
    if (!s3Key?.length) {
        return;
    }

    const keysToMigrate = [
        appConstants.localStorage.courseFirebaseToken,
        appConstants.localStorage.webcastsFirebaseToken,
    ];

    for (const key of keysToMigrate) {
        try {
            const encryptedVal = localStorage.getItem(key);
            if (encryptedVal?.length && !isAesGcmEncrypted(encryptedVal)) {
                const plaintext = encryptAndDecryptXorString(
                    encryptedVal,
                    s3Key,
                );
                if (plaintext?.length && plaintext.length > 20) {
                    await setAesEncryptedLocalStorageItem(plaintext, key);
                } else {
                    localStorage.removeItem(key);
                }
            }
        } catch (e) {
            console.error(`AES-GCM migration failed for ${key}:`, e);
            localStorage.removeItem(key);
        }
    }

    setAesGcmMigrated("true");
};

const migrateNgTokensXorToAesGcm = async (): Promise<void> => {
    const s3Key = config.s3.xor;
    if (!s3Key?.length) {
        return;
    }

    const keysToMigrate = [
        appConstants.localStorage.ngAccessToken,
        appConstants.localStorage.ngRefreshToken,
    ];

    for (const key of keysToMigrate) {
        try {
            const encryptedVal = localStorage.getItem(key);
            if (!encryptedVal?.length || isAesGcmEncrypted(encryptedVal)) {
                continue;
            }
            const plaintext = await decryptAutoDetect(encryptedVal, s3Key);
            if (plaintext?.length && plaintext.length > 20) {
                await setAesEncryptedLocalStorageItem(plaintext, key);
            } else {
                localStorage.removeItem(key);
            }
        } catch (e) {
            console.error(
                `AES-GCM migration failed for ng token ${key}:`,
                e,
            );
            localStorage.removeItem(key);
        }
    }

    setNgTokensAesGcmMigrated("true");
};

export const setAesEncryptedLocalStorageItem = async (
    value: string,
    key: string,
): Promise<void> => {
    const s3Key = config.s3.xor;
    if (!value?.length || !s3Key?.length) {
        localStorage.setItem(key, "");
        return;
    }
    const encryptedVal = await encryptAesGcm(value, s3Key);
    localStorage.setItem(key, encryptedVal);
};

export const setLocalStorageUserName = (
    firstName: string,
    lastName: string,
) => {
    localStorage.setItem(appConstants.localStorage.firstName, firstName);
    localStorage.setItem(appConstants.localStorage.lastName, lastName);
    config.user.firstName = firstName;
    config.user.lastName = lastName;
    localStorage.setItem(
        appConstants.localStorage.usernameSyncTime,
        moment.now().toString(),
    );
};

export const updateNgTokens = async (
    ngAccessToken: string,
    ngRefreshToken: string,
): Promise<void> => {
    config.ngAccesstoken = ngAccessToken;
    config.ngRefreshToken = ngRefreshToken;
    await Promise.all([
        setAesEncryptedLocalStorageItem(
            ngAccessToken,
            appConstants.localStorage.ngAccessToken,
        ),
        setAesEncryptedLocalStorageItem(
            ngRefreshToken,
            appConstants.localStorage.ngRefreshToken,
        ),
    ]);
};
